import PropTypes from 'prop-types';
import React from 'react';
import {
  RadioWrapper,
  RadioLabel,
  RadioOption,
  HiddenInput,
  RadioCircle,
  RadioText,
  HelperText,
} from './StyledComponents';

/**
 * Radio - A themed radio button group component
 *
 * @param {string} name - Name attribute for the radio group
 * @param {string} label - Label for the radio group
 * @param {string|number} value - Currently selected value
 * @param {Array} options - Array of options {label, value, disabled?}
 * @param {Function} onChange - Called when selection changes
 * @param {boolean} inline - Display options inline (horizontal)
 * @param {boolean} disabled - Disable all options
 * @param {string} helperText - Helper text below the radio group
 * @param {boolean} error - Show error state
 */
const Radio = ({
  name,
  label,
  value,
  options = [],
  onChange,
  inline = false,
  disabled = false,
  helperText,
  error,
  ...props
}) => {
  const handleChange = (optionValue) => {
    if (disabled) return;
    if (onChange) {
      // Support both event-style and direct value callbacks
      onChange({
        target: {
          name,
          value: optionValue,
        },
      });
    }
  };

  return (
    <div {...props}>
      {label && <RadioLabel>{label}</RadioLabel>}
      <RadioWrapper inline={inline}>
        {options.map((option) => {
          const isChecked = value === option.value;
          const isDisabled = disabled || option.disabled;

          return (
            <RadioOption
              key={option.value}
              selected={isChecked}
              disabled={isDisabled}
              onClick={() => !isDisabled && handleChange(option.value)}
            >
              <HiddenInput
                type="radio"
                name={name}
                value={option.value}
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => handleChange(option.value)}
              />
              <RadioCircle checked={isChecked} />
              <RadioText>{option.label}</RadioText>
            </RadioOption>
          );
        })}
      </RadioWrapper>
      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </div>
  );
};

Radio.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  onChange: PropTypes.func,
  inline: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  error: PropTypes.bool,
};

export default Radio;
